import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { documentationUrl, githubUrl, supportUrl } from '@/lib/links'
import { Logo } from '@/components/generals/logo'
import Container from '@/components/generals/container'
import Reveal from '@/components/generals/reveal'
import SmartLink from '@/components/generals/smart-link'

import { researchDisclaimer, siteConfig } from '@/constants/site'

const groups = [
    {
        title: 'Product',
        links: [
            { label: 'Overview', href: '/#overview' },
            { label: 'Features', href: '/#features' },
            { label: 'Screenshots', href: '/#screenshots' },
            { label: 'Download', href: '/download' }
        ]
    },
    {
        title: 'Resources',
        links: [
            { label: "Release Notes", href: '/releases' },
            { label: "Installation Help", href: '/install' },
            { label: 'Documentation', href: documentationUrl },
            { label: 'GitHub', href: githubUrl }
        ]
    },
    {
        title: "Legal and support",
        links: [
            { label: "Privacy Policy", href: '/privacy' },
            { label: 'License', href: '/license' },
            { label: "Contact / Support", href: supportUrl }
        ]
    }
]

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='on-dark bg-chocolate-deep text-ivory'>
            <Container className='py-16 lg:py-20'>
                <Reveal variant='fade'>
                    <div className='grid gap-14 lg:grid-cols-12'>
                        <div className='lg:col-span-4'>
                            <Link href='/' aria-label="Ocuvia, home" className='inline-block rounded-md'>
                                <Logo tone='dark' />
                            </Link>

                            <p className='mt-5 max-w-xs text-[15px] leading-relaxed text-ivory/70'>
                                {siteConfig.mission}
                            </p>
                        </div>

                        <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8'>
                            {groups.map(group => (
                                <div key={group.title}>
                                    <h2 className='text-[11.5px] font-medium uppercase tracking-[0.2em] text-gold'>
                                        {group.title}
                                    </h2>

                                    <ul className='mt-5 space-y-3.5'>
                                        {group.links.map(link => (
                                            <li key={link.label}>
                                                <SmartLink
                                                    href={link.href}
                                                    tone='dark'
                                                    className='text-[14.5px] text-ivory/75 transition-colors duration-300 hover:text-ivory'
                                                    pendingClassName='text-[14.5px] text-ivory/45'
                                                >
                                                    {link.label}
                                                </SmartLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </nav>
                    </div>

                    <div className='mt-16 border-t border-ivory/10 pt-8'>
                        <p className='max-w-3xl text-[13px] leading-relaxed text-ivory/60'>
                            {researchDisclaimer}
                        </p>

                        <div className='mt-6 flex flex-col gap-2 text-[13px] text-ivory/55 sm:flex-row sm:items-center sm:justify-between'>
                            <p>
                                © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Ocuvia
                            </p>

                            <p>
                                For research use only.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </Container>
        </footer>
    )
}

export default SiteFooter
